import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createReportOverviewDialogAtom } from '../atoms';
import { RichTextPreview } from './RichText';
import { NewReportForm } from '../lib/schemas';
import { FilePreview } from './FilePreview';

type Props = {
  onSubmit: (report: NewReportForm) => void;
};

export function CreateReportOverviewDialog({ onSubmit }: Props) {
  const { t } = useTranslation();
  const [state, setState] = useAtom(createReportOverviewDialogAtom);
  const dialog = useRef<HTMLDialogElement | null>(null);

  const handleClose = () => {
    setState({ isOpen: false, report: undefined });
    dialog.current?.close();
  };

  useEffect(() => {
    if (!dialog.current?.open && state.isOpen) dialog.current?.showModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  return (
    <dialog ref={dialog} className="max-w-xl">
      <h3 className="text-hero">{t('report.overviewDialog.subtitle')}</h3>
      <span className="whitespace-normal">{t('report.overviewDialog.description')}</span>
      <h3 className="text subtitle font-semibold">{t('report.overviewDialog.mainInfo')}</h3>
      <div className="grid grid-cols-[max-content_auto] grid-rows-3 items-center gap-x-8 gap-y-2">
        <span className="font-semibold">{t('report.name')}:</span>
        <span>{state.report?.name}</span>
        <span className="font-semibold">{t('report.priority')}:</span>
        <span>{state.report?.priority}</span>
      </div>
      <div className="flex items-center">
        <RichTextPreview value={state.report?.description ?? ''} />
      </div>
      <FilePreview
        files={[
          ...(state.report?.currentScreenshots || []),
          ...(state.report?.expectedScreenshots || []),
        ]}
      />
      <div className="flex justify-end gap-2">
        <button
          className="primary-outline"
          onClick={() => {
            handleClose();
          }}
        >
          {t('common.cancel')}
        </button>
        <button
          className="primary-fill"
          onClick={() => {
            handleClose();
            onSubmit(state.report as NewReportForm);
          }}
        >
          {t('common.fine')}
        </button>
      </div>
    </dialog>
  );
}
